import { Injectable, signal } from '@angular/core';
import { EInvoiceDigitalHubForm, EInvoiceDigitalHubFormGroup, EInvoiceForm, EInvoiceFormGroup, EInvoiceFtpForm, EInvoiceFtpFormGroup, EInvoiceMerchantFields, EInvoiceValidators } from './e-invoice-setup.model';
import { taxRegimes } from 'src/app/core/constants';
import { CustomFormControl, CustomFormGroup } from '@tilby/tilby-ui-lib/components/tilby-magic-form';
import { AbstractControl, Validators } from '@angular/forms';
import { merge } from 'rxjs';

@Injectable()
export class EInvoiceSetupFormService {

  private merchantForm!: EInvoiceFormGroup;
  private digitalHubForm!: EInvoiceDigitalHubFormGroup;
  private ftpForm!: EInvoiceFtpFormGroup;

  merchantFormReady = signal(false);
  digitalHubFormReady = signal(false);
  ftpFormReady = signal(false);

  getMerchantForm() {
    return this.merchantForm;
  }

  getDigitalHubForm() {
    return this.digitalHubForm;
  }

  getFtpForm() {
    return this.ftpForm;
  }

  validators: EInvoiceValidators = {
    companyNameMaxLength: 80,
    firstNameMaxLength: 60,
    lastNameMaxLength: 60,
    countryMinLength: 2,
    countryMaxLength: 2,
    vatMaxLength: 28,
    taxCodeMinLength: 11,
    taxCodeMaxLength: 16,
    addressStreetMaxLength: 60,
    addressNumberMaxLength: 8,
    addressZipMinLength: 5,
    addressZipMaxLength: 5,
    addressCityMaxLength: 60,
    addressProvMinLength: 2,
    addressProvMaxLength: 2,
    addressCountryMinLength: 2,
    addressCountryMaxLength: 2,
    phoneMinLength: 5,
    phoneMaxLength: 12,
    faxMinLength: 5,
    faxMaxLength: 12,
    emailMinLength: 7,
    emailMaxLength: 256,
    emailPecMinLength: 7,
    emailPecMaxLength: 256,
    bankMerchantNameMaxLength: 200,
    bankNameMaxLength: 80,
    bankIbanMaxLength: 34,
    portMinValue: 0,
    portMaxValue: 65535
  };

  taxRegimes = taxRegimes; 

  createForms(data: Partial<EInvoiceMerchantFields> = {}) {
    this.merchantForm = this.createMerchantForm(data);
    this.digitalHubForm = this.createDigitalHubForm();
    this.ftpForm = this.createFtpForm();
  }

  private createMerchantForm(data: Partial<EInvoiceMerchantFields> = {}) {
    this.merchantForm = new CustomFormGroup<EInvoiceForm>({
      'e_invoice.invoice_prefix': new CustomFormControl(
        data['e_invoice.invoice_prefix'] || ""
      ),
      'e_invoice.merchant.company_name': new CustomFormControl(
        data['e_invoice.merchant.company_name'] || "", 
        { validators: [Validators.required, Validators.maxLength(this.validators.companyNameMaxLength)]}
      ),
      'e_invoice.merchant.first_name': new CustomFormControl(
        data['e_invoice.merchant.first_name'] || "", 
        { validators: [Validators.maxLength(this.validators.firstNameMaxLength)] }
      ),
      'e_invoice.merchant.last_name': new CustomFormControl(
        data['e_invoice.merchant.last_name'] || "", 
        { validators: [Validators.maxLength(this.validators.lastNameMaxLength)] }
      ),
      'e_invoice.merchant.country': new CustomFormControl(
        data['e_invoice.merchant.country'] || 'IT', 
        { validators: [Validators.required, Validators.minLength(this.validators.countryMinLength), Validators.maxLength(this.validators.countryMaxLength)] }
      ),
      'e_invoice.merchant.vat_code': new CustomFormControl(
        data['e_invoice.merchant.vat_code'] || "", 
        { validators: [Validators.required, Validators.maxLength(this.validators.vatMaxLength)] }
      ),
      'e_invoice.merchant.tax_code': new CustomFormControl(
        data['e_invoice.merchant.tax_code'] || "", 
        { validators: [Validators.minLength(this.validators.taxCodeMinLength), Validators.maxLength(this.validators.taxCodeMaxLength)] }
      ),
      'e_invoice.merchant.tax_regime': new CustomFormControl(
        data['e_invoice.merchant.tax_regime'] || "", 
        { validators: [Validators.required] }
      ),
        
      // Sede legale
      'e_invoice.merchant.head_office.street': new CustomFormControl(
        data['e_invoice.merchant.head_office.street'] || "", 
        { validators: [Validators.required, Validators.maxLength(this.validators.addressStreetMaxLength)] }
      ),
      'e_invoice.merchant.head_office.number': new CustomFormControl(
        data['e_invoice.merchant.head_office.number'] || "", 
        { validators: [Validators.required, Validators.maxLength(this.validators.addressNumberMaxLength)] }
      ),
      'e_invoice.merchant.head_office.zip': new CustomFormControl(
        data['e_invoice.merchant.head_office.zip'] || "", 
        { validators: [Validators.required, Validators.minLength(this.validators.addressZipMinLength), Validators.maxLength(this.validators.addressZipMaxLength)] }
      ),
      'e_invoice.merchant.head_office.city': new CustomFormControl(
        data['e_invoice.merchant.head_office.city'] || "", 
        { validators: [Validators.required, Validators.maxLength(this.validators.addressCityMaxLength)] }
      ),
      'e_invoice.merchant.head_office.prov': new CustomFormControl(
        data['e_invoice.merchant.head_office.prov'] || "", 
        { validators: [Validators.required, Validators.minLength(this.validators.addressProvMinLength), Validators.maxLength(this.validators.addressProvMaxLength)] }
      ),
      'e_invoice.merchant.head_office.country': new CustomFormControl(
        data['e_invoice.merchant.head_office.country'] || 'IT', 
        { validators: [Validators.required, Validators.minLength(this.validators.addressCountryMinLength), Validators.maxLength(this.validators.addressCountryMaxLength)] }
      ),

      // Contatti
      'e_invoice.merchant.phone': new CustomFormControl(
        data['e_invoice.merchant.phone'] || "",
        { validators: [Validators.minLength(this.validators.phoneMinLength), Validators.maxLength(this.validators.phoneMaxLength)] }
      ),
      'e_invoice.merchant.fax': new CustomFormControl(
        data['e_invoice.merchant.fax'] || "",
        { validators: [Validators.minLength(this.validators.faxMinLength), Validators.maxLength(this.validators.faxMaxLength)] }
      ),
      'e_invoice.merchant.email': new CustomFormControl(
        data['e_invoice.merchant.email'] || "", 
        { validators: [Validators.email, Validators.minLength(this.validators.emailMinLength), Validators.maxLength(this.validators.emailMaxLength)] }
      ),
      'e_invoice.merchant.email_pec': new CustomFormControl(
        data['e_invoice.merchant.email_pec'] || "", 
        { validators: [Validators.email, Validators.minLength(this.validators.emailPecMinLength), Validators.maxLength(this.validators.emailPecMaxLength)] }
      ),

      // Banca
      'e_invoice.bank_merchant_name': new CustomFormControl(
        data['e_invoice.bank_merchant_name'] || ""
      ),
      'e_invoice.bank_name': new CustomFormControl(
        data['e_invoice.bank_name'] || ""
      ),
      'e_invoice.bank_iban': new CustomFormControl(
        data['e_invoice.bank_iban'] || ""
      )
    });

    this.setupBankLogic();

    this.merchantFormReady.set(true);
    return this.merchantForm;
  }

  private setupBankLogic() {
    const merchantName = this.bankMerchantName;
    const bankName = this.bankName;
    const bankIban = this.bankIban;

    if (!merchantName || !bankName || !bankIban) {
      return;
    }

    merge(
      merchantName.valueChanges,
      bankName.valueChanges,
      bankIban.valueChanges
    ).subscribe(() => {
      const isAnyFilled = !!(merchantName.value || bankName.value || bankIban.value);
      this.applyValidators(bankName, isAnyFilled, [Validators.maxLength(this.validators.bankNameMaxLength)]);
      this.applyValidators(bankIban, isAnyFilled, [Validators.maxLength(this.validators.bankIbanMaxLength)]);
    });

    this.bankName.updateValueAndValidity();
  }

  private applyValidators(control: AbstractControl | null, shouldBeRequired: boolean, otherValidators: any[]) {
    if (!control) return;

    const newValidators = shouldBeRequired 
        ? [Validators.required, ...otherValidators] 
        : [...otherValidators];

    control.setValidators(newValidators);
    
    control.updateValueAndValidity({ emitEvent: false });
  }

  private createDigitalHubForm() {
    this.digitalHubForm = new CustomFormGroup<EInvoiceDigitalHubForm>({
      userName: new CustomFormControl(null, 
        { validators: [Validators.required] }
      ),
      password: new CustomFormControl(
        null, 
        { validators: [Validators.required] }
      )
    });

    this.digitalHubFormReady.set(true);
    return this.digitalHubForm;
  }

  private createFtpForm() {
    this.ftpForm = new CustomFormGroup<EInvoiceFtpForm>({
      protocol: new CustomFormControl(
        null, 
        { validators: [Validators.required] }
      ),
      host: new CustomFormControl(
        null, 
        { validators: [Validators.required] }
      ),
      port: new CustomFormControl(
        null,
        { validators: [Validators.min(this.validators.portMinValue), Validators.max(this.validators.portMaxValue)] }
      ),
      user: new CustomFormControl(
        null, 
        { validators: [Validators.required] }
      ),
      password: new CustomFormControl(
        null, 
        { validators: [Validators.required] }
      ),
      path: new CustomFormControl(null),
      secure: new CustomFormControl(false)
    });

    this.ftpFormReady.set(true);
    return this.ftpForm;
  }

  get invoicePrefix() {
    return this.merchantForm.controls['e_invoice.invoice_prefix'];
  }

  get companyName() {
    return this.merchantForm.controls['e_invoice.merchant.company_name'];
  }

  get firstName() {
    return this.merchantForm.controls['e_invoice.merchant.first_name'];
  }
  
  get lastName() {
    return this.merchantForm.controls['e_invoice.merchant.last_name'];
  }
  
  get country() {
    return this.merchantForm.controls['e_invoice.merchant.country'];
  }
  
  get vatCode() {
    return this.merchantForm.controls['e_invoice.merchant.vat_code'];
  }
  
  get taxCode() {
    return this.merchantForm.controls['e_invoice.merchant.tax_code'];
  }
  
  get taxRegime() {
    return this.merchantForm.controls['e_invoice.merchant.tax_regime'];
  }

  get addressStreet() {
    return this.merchantForm.controls['e_invoice.merchant.head_office.street'];
  }
  
  get addressNumber() {
    return this.merchantForm.controls['e_invoice.merchant.head_office.number'];
  }
  
  get addressZip() {
    return this.merchantForm.controls['e_invoice.merchant.head_office.zip'];
  }
  
  get addressCity() {
    return this.merchantForm.controls['e_invoice.merchant.head_office.city'];
  }
  
  get addressProv() {
    return this.merchantForm.controls['e_invoice.merchant.head_office.prov'];
  }
  
  get addressCountry() {
    return this.merchantForm.controls['e_invoice.merchant.head_office.country'];
  }
  
  get phone() {
    return this.merchantForm.controls['e_invoice.merchant.phone'];
  }
  
  get fax() {
    return this.merchantForm.controls['e_invoice.merchant.fax'];
  }
  
  get email() {
    return this.merchantForm.controls['e_invoice.merchant.email'];
  }
  
  get emailPec() {
    return this.merchantForm.controls['e_invoice.merchant.email_pec'];
  }
  
  get bankMerchantName() {
    return this.merchantForm.controls['e_invoice.bank_merchant_name'];
  }
  
  get bankName() {
    return this.merchantForm.controls['e_invoice.bank_name'];
  }
  
  get bankIban() {
    return this.merchantForm.controls['e_invoice.bank_iban'];
  }

  get digitalHubUserName() {
    return this.digitalHubForm.controls.userName;
  }

  get digitalHubPassword() {
    return this.digitalHubForm.controls.password;
  }

  get ftpProtocol() {
    return this.ftpForm.controls.protocol;
  }

  get ftpHost() {
    return this.ftpForm.controls.host;
  }

  get ftpPort() {
    return this.ftpForm.controls.port;
  }

  get ftpUser() {
    return this.ftpForm.controls.user;
  }

  get ftpPassword() {
    return this.ftpForm.controls.password;
  }

  get ftpPath() {
    return this.ftpForm.controls.path;
  }

  get ftpSecure() {
    return this.ftpForm.controls.secure;
  }
}
